import { SPARK_RESOURCE_TYPES } from 'constants/common';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { useWorkspaceContext } from 'hooks/common';
import { useQuery } from 'react-query';
import toastr from 'react-hot-toast';
import type { PluginConnectionsResponse } from 'types/PluginConnections';
import {
  FilterParameters,
  generateFilterExpression,
  generateSortQuery,
  generateURL,
} from 'utilities/hooks/urlGeneration';
import { fetchWrapper } from 'utils/fetchWrapper';

const getPluginConnections = (
  pluginID: string | null | undefined,
  currentWorkspaceID: string | null | undefined,
  pluginAuthenticationID: string | null | undefined
) => {
  if (!pluginID || !currentWorkspaceID) {
    return Promise.reject('Plugin ID is required.');
  }

  const andFilterConfig: FilterParameters[] = [
    {
      type: 'equal',
      key: 'workspaceID',
      value: currentWorkspaceID,
    },
  ];

  if (pluginAuthenticationID) {
    andFilterConfig.push({
      type: 'equal',
      key: 'pluginAuthenticationID',
      value: pluginAuthenticationID,
    });
  }

  if (pluginID) {
    andFilterConfig.push({
      type: 'equal',
      key: 'pluginID',
      value: pluginID,
    });
  }

  const queryArray = [
    generateSortQuery('createdAt', 'Decs'),
    generateFilterExpression('and', andFilterConfig),
  ];
  const url = generateURL(SPARK_RESOURCE_TYPES.PLUGIN_CONNECTIONS, queryArray);

  return fetchWrapper<PluginConnectionsResponse>({
    url: url,
    method: 'GET',
  });
};

export const usePluginConnections = (
  pluginID: string | null | undefined,
  pluginAuthenticationID: string | null | undefined
) => {
  const { currentWorkspaceID } = useWorkspaceContext();
  const query = useQuery(
    ['pluginConnections', pluginID, pluginAuthenticationID],
    () =>
      getPluginConnections(
        pluginID,
        currentWorkspaceID,
        pluginAuthenticationID
      ),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`failed to Fetch Plugins connection: ${error.message}`);
        }
      },
      enabled: Boolean(pluginID),
      ...CACHE_AND_STALE_TIME,
    }
  );
  const { data } = query;
  const { data: pluginConnections } = data || {};

  return {
    pluginConnections,
    ...query,
  };
};
